// Dedicated recovery routes for the typed auth error taxonomy (ADR-018).
//
// Each route presents a brand-consistent screen with role="alert" + 3 CTAs:
//   - Primary: re-issue a magic link (calls supabase.auth.signInWithOtp).
//   - Secondary: navigate fresh to /login (clean-slate sign-in).
//   - Tertiary: mailto support@solomonadvising.com.
//
// We deliberately do NOT auto-trigger the re-issue. The user has to opt in
// (clicks the button), which:
//   1. Avoids accidental email floods on rapid back-button navigation.
//   2. Keeps the rate-limit behaviour predictable from the user's POV.
//   3. Matches the enumeration-protection contract used on /login: identical
//      success message regardless of whether the address is provisioned.

import { useEffect, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';
import { supabase } from '../lib/supabase';
import { logger } from '../lib/logger';
import { writeAuditEvent } from '../lib/audit';
import { AuthErrorLayout } from '../components/auth/AuthErrorLayout';

const REDIRECT_URL =
  typeof window !== 'undefined' ? `${window.location.origin}/auth/callback` : '';

type ReissueState = 'idle' | 'sending' | 'sent';

interface RecoveryConfig {
  title: string;
  description: ReactNode;
  /** Audit event_type emitted on render (warning that the user landed here). */
  auditEventType: string;
}

function useReissueLink(reasonForAudit: string): {
  state: ReissueState;
  email: string;
  setEmail: (next: string) => void;
  sendLink: () => Promise<void>;
} {
  const [searchParams] = useSearchParams();
  const initialEmail = searchParams.get('email') ?? '';
  const [email, setEmail] = useState<string>(initialEmail);
  const [state, setState] = useState<ReissueState>('idle');

  async function sendLink(): Promise<void> {
    if (!email || state === 'sending') return;
    setState('sending');
    try {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: { emailRedirectTo: REDIRECT_URL, shouldCreateUser: false },
      });
      if (error) {
        // We deliberately do NOT distinguish error vs success in the UI
        // (enumeration protection — same contract as /login).
        logger.warn({
          source: 'auth-error.reissue',
          message: 'signInWithOtp returned error (UI hidden for enumeration protection)',
          payload: { code: error.code ?? null, reason: reasonForAudit },
        });
      }
    } catch (err: unknown) {
      logger.error({
        source: 'auth-error.reissue',
        message: 'signInWithOtp threw',
        payload: { error: String(err), reason: reasonForAudit },
      });
    } finally {
      setState('sent');
    }
  }

  return { state, email, setEmail, sendLink };
}

function RecoveryScreen({
  config,
  reason,
}: {
  config: RecoveryConfig;
  reason: string;
}): ReactNode {
  const { state, email, setEmail, sendLink } = useReissueLink(reason);

  // Emit audit event once on mount (ADR-018). v1 audit is fire-and-forget
  // logger output, so a StrictMode double-invocation is harmless; v2 will
  // dedupe at the writer endpoint.
  useEffect(() => {
    void writeAuditEvent({
      event_type: config.auditEventType,
      payload: { route: 'auth-error' },
    });
  }, [config.auditEventType]);

  const description = (
    <div className="flex flex-col gap-4">
      <p>{config.description}</p>
      {state === 'sent' ? (
        <p
          role="status"
          aria-live="polite"
          className="rounded-sm bg-white border border-border-light p-4 text-text-body"
        >
          If that address has access, a sign-in link is on its way. Check your
          inbox (and spam folder, just in case).
        </p>
      ) : (
        <label className="block text-left">
          <span className="mb-1 block text-[13px] font-medium text-text-dark">
            Email
          </span>
          <input
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(ev) => setEmail(ev.target.value)}
            className="w-full rounded-sm border border-border-strong bg-white px-3 py-2 text-[15px] focus-visible:outline-2 focus-visible:outline-orange"
            placeholder="you@yourcompany.com"
          />
        </label>
      )}
    </div>
  );

  const primaryLabel =
    state === 'sending'
      ? 'Sending…'
      : state === 'sent'
        ? 'Re-send'
        : 'Send me a new link';

  return (
    <AuthErrorLayout
      title={config.title}
      description={description}
      primary={{
        label: primaryLabel,
        onClick: () => {
          void sendLink();
        },
      }}
      secondary={{ label: 'Sign in fresh', href: '/login' }}
      tertiary={{
        label: 'Email support@solomonadvising.com',
        href: 'mailto:support@solomonadvising.com',
      }}
    />
  );
}

export function AuthExpiredRoute(): ReactNode {
  return (
    <RecoveryScreen
      reason="link_expired"
      config={{
        title: 'Your sign-in link expired',
        description:
          'Magic links expire 15 minutes after we send them. No problem — request a fresh one and we’ll have you back in.',
        auditEventType: 'auth.error.link_expired',
      }}
    />
  );
}

export function AuthInvalidLinkRoute(): ReactNode {
  return (
    <RecoveryScreen
      reason="link_invalid"
      config={{
        title: 'We couldn’t verify this sign-in link',
        description:
          'This link looks malformed or has been tampered with. Request a new one and we’ll send a fresh sign-in link to your inbox.',
        auditEventType: 'auth.error.link_invalid',
      }}
    />
  );
}

export function AuthAlreadyUsedRoute(): ReactNode {
  return (
    <RecoveryScreen
      reason="link_consumed"
      config={{
        title: 'This sign-in link has already been used',
        description:
          'Magic links are single-use. If your other tab still has a session, sign in there. Otherwise, request a fresh link below.',
        auditEventType: 'auth.error.link_consumed',
      }}
    />
  );
}

export function AuthGenericErrorRoute(): ReactNode {
  return (
    <RecoveryScreen
      reason="unknown"
      config={{
        title: 'We couldn’t verify your access right now',
        description:
          'This is usually a brief network hiccup or a mid-flight retry. Request a new link or sign in fresh.',
        auditEventType: 'auth.error.unknown',
      }}
    />
  );
}
